/* eslint-disable @typescript-eslint/no-use-before-define */
import { createSlice, type PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { randomId } from '@/utils/randomId';
import { supportCallbackKey } from '@/utils/notificationCallbacks';

const defaultDuration = 5000;

export const NotificationTypes = {
  SUCCESS: 'success',
  ERROR: 'error',
} as const;

type OneOfNotificationTypes = (typeof NotificationTypes)[keyof typeof NotificationTypes];

export interface Notification {
  id: string;
  text: string;
  type: OneOfNotificationTypes;
  duration: number;
}

export interface NotificationsState {
  notifications: Notification[];
}

interface RequestedNotification {
  text: string;
  type?: OneOfNotificationTypes;
  duration?: number;
}
export const showSuccessNotification = (text: string, duration = defaultDuration) =>
  showNotification({ text, duration, type: NotificationTypes.SUCCESS });
export const showErrorNotification = (text: string, duration = defaultDuration) =>
  showNotification({ text, duration, type: NotificationTypes.ERROR });
export const showNotificationWithSupportLink = (
  text: string,
  duration = defaultDuration,
  type = NotificationTypes.ERROR,
) => showNotification({ text: `${text}  [contact support](${supportCallbackKey}`, duration, type });

const initialState: NotificationsState = { notifications: [] };

export const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    addNotification: (state, action: PayloadAction<Notification>) => {
      state.notifications.push(action.payload);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter((n) => n.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(showNotification.pending, () => {});
  },
});

export const showNotification = createAsyncThunk<
  void,
  RequestedNotification,
  { state: { [notificationsSlice.name]: NotificationsState } }
>('showNotification', (arg, { dispatch }) => {
  const id = randomId();

  const notification: Notification = {
    type: NotificationTypes.ERROR,
    duration: defaultDuration,
    ...arg,
    id,
  };

  dispatch(notificationsSlice.actions.addNotification(notification));

  setTimeout(() => dispatch(notificationsSlice.actions.removeNotification(id)), notification.duration);
});

export const { removeNotification } = notificationsSlice.actions;
